import { useTranslation } from "react-i18next";
import { CustomTypographyLabelValue } from "../CustomTypographyLabelValue/CustomTypographyLabelValue";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import { FC } from "react";
import { CustomIconButton } from "../CustomIconButton/CustomIconButton";
import useStyles from "./styles";
import { useParams } from "react-router";
import { useGetOneVehicle } from "../../api/vehicule";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import dayjs from "dayjs";
import { TelematicsBadge } from "../TelematicsBadge/TelematicsBadge";

type props = {
  isEditableIcon?: boolean;
  onClickEditIcon?: () => void;
  /**
   * Explicit vehicle to render. Optional so existing call sites on
   * /vehicule/update/:id keep working unchanged, but required anywhere the
   * component appears outside that route -- the TCO page has no :id segment,
   * so falling back to useParams() there produced parseInt("") === NaN and a
   * request to /fleets/{fleet}/vehicles/NaN.
   */
  vehicleId?: number;
};

export const CustomVehicleInfos: FC<props> = ({
  isEditableIcon,
  onClickEditIcon,
  vehicleId: vehicleIdProp,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { id = "" } = useParams();
  const vehicleId = vehicleIdProp ?? parseInt(id);
  const { vehicule } = useGetOneVehicle(getCurrentFleetId(), vehicleId);

  // Helper function to format real-time data values
  const formatRealTimeValue = (value: any, unit?: string) => {
    if (value === null || value === undefined) return t("common.notAvailable", "N/A");
    return unit ? `${value} ${unit}` : value.toString();
  };

  // Helper function to get status color/style
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'active':
      case 'running':
        return { color: '#4CAF50' }; // Green
      case 'idle':
        return { color: '#FF9800' }; // Orange
      case 'inactive':
      case 'parked':
        return { color: '#757575' }; // Gray
      case 'unknown':
      default:
        return { color: '#F44336' }; // Red
    }
  };

  // Helper function to check if real-time data has any valid values
  const hasValidRealTimeData = () => {
    if (!vehicule?.realTimeData) {
      return false;
    }

    const { 
      speed, 
      fuelLevelPercentage, 
      batteryLevel, 
      ignition,
      odometer,
      engineSpeed,
      engineTemperature,
      heading,
      latitude,
      longitude,
      timestamp
    } = vehicule.realTimeData;

    const hasValidData = 
      (speed !== null && speed !== undefined) ||
      (fuelLevelPercentage !== null && fuelLevelPercentage !== undefined) ||
      (batteryLevel !== null && batteryLevel !== undefined) ||
      (ignition !== null && ignition !== undefined) ||
      (odometer !== null && odometer !== undefined) ||
      (engineSpeed !== null && engineSpeed !== undefined) ||
      (engineTemperature !== null && engineTemperature !== undefined) ||
      (heading !== null && heading !== undefined) ||
      (latitude !== null && latitude !== undefined) ||
      (longitude !== null && longitude !== undefined) ||
      (timestamp !== null && timestamp !== undefined);

    return hasValidData;
  };

  return (
    <div className={classes.root}>
      <div className={classes.subContainerInfo}>
        <div className={classes.code}>
          <CustomTypography
            text={vehicule?.immatriculation ?? ""}
            variant={"h3"}
          />
        </div>
        <div className={classes.description}>
        <div className={classes.paperTitle}>
          <CustomTypography
            isUppercase
            variant="h3"
            text={t("vehicule.vehicule", "Vehicle")}
          />
          {isEditableIcon && (
            <CustomIconButton
              iconName={"pencil"}
              widthIcon={"20px"}
              heightIcon={"20px"}
              onClick={onClickEditIcon}
            />
          )}
        </div>
        <div className={classes.mark}>
          <div>
            <CustomTypographyLabelValue
              label={t("panel.vehicule.brand", "Brand")}
              value={(() => {
                if (!vehicule?.brandModel) return "";
                
                // If it's already an object, use it directly
                if (typeof vehicule.brandModel === 'object') {
                  return vehicule?.brandModel?.brand?.name ?? "";
                }
                
                // If it's a string, parse it
                try {
                  return JSON.parse(vehicule.brandModel)?.brand?.name ?? "";
                } catch (e) {
                  return "";
                }
              })()}
            />
            <CustomTypographyLabelValue
              label={t("panel.vehicule.model", "Model")}
              value={(() => {
                if (!vehicule?.brandModel) return "";
                
                if (typeof vehicule.brandModel === 'object') {
                  return vehicule.brandModel.name ?? "";
                }
                
                try {
                  return JSON.parse(vehicule.brandModel).name ?? "";
                } catch (e) {
                  return "";
                }
              })()}
            />
            <CustomTypographyLabelValue
              label={t("vehicule.carburant")}
              value={vehicule?.carburant ?? ""}
            />
            <CustomTypographyLabelValue
              label={t("panel.vehicule.fiscalPower", "Fiscal power")}
              value={`${vehicule?.puissance ?? ""} CV`}
            />
          </div>
          <div>
            <CustomTypographyLabelValue
              label={t("panel.vehicule.box", "Box")}
              value={vehicule?.boite ?? ""}
            />
            <CustomTypographyLabelValue
              label={t("vehicule.vin", "Vin")}
              value={vehicule?.vin ?? ""}
            />
            <CustomTypographyLabelValue
              label={t(
                "panel.vehicule.dateOfCirculation",
                "Date of circulation"
              )}
              value={
                vehicule?.mise_en_circulation
                  ? dayjs(vehicule.mise_en_circulation).format("DD-MM-YYYY")
                  : ""
              }
            />
          </div>
        </div>

        {/* Real-time Data Section - Only show if there's valid data */}
        {hasValidRealTimeData() && vehicule?.realTimeData && (
          <>
            <div className={classes.mark}>
              <div>
                <CustomTypographyLabelValue
                  label={t("vehicule.status", "Status")}
                  value={
                    <span style={getStatusStyle(vehicule.realTimeData.status)}>
                      {t(`vehicule.status.${vehicule.realTimeData.status}`, 
                         vehicule.realTimeData.status)}
                    </span>
                  }
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.speed", "Speed")}
                  value={formatRealTimeValue(vehicule.realTimeData.speed, "km/h")}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.fuelLevel", "Fuel Level")}
                  value={formatRealTimeValue(
                    vehicule.realTimeData.fuelLevelPercentage, 
                    "%"
                  )}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.batteryLevel", "Battery Level")}
                  value={formatRealTimeValue(vehicule.realTimeData.batteryLevel, "V")}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.ignition", "Ignition")}
                  value={vehicule.realTimeData.ignition ? 
                    t("common.on", "On") : 
                    t("common.off", "Off")
                  }
                />
              </div>
              <div>
                <CustomTypographyLabelValue
                  label={t("vehicule.odometer", "Odometer")}
                  value={formatRealTimeValue(vehicule.realTimeData.odometer, "km")}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.engineSpeed", "Engine Speed")}
                  value={formatRealTimeValue(vehicule.realTimeData.engineSpeed, "RPM")}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.engineTemperature", "Engine Temperature")}
                  value={formatRealTimeValue(vehicule.realTimeData.engineTemperature, "°C")}
                />
                <CustomTypographyLabelValue
                  label={t("vehicule.heading", "Heading")}
                  value={formatRealTimeValue(vehicule.realTimeData.heading, "°")}
                />
                {vehicule.realTimeData.timestamp && (
                  <CustomTypographyLabelValue
                    label={t("vehicule.lastUpdate", "Last Update")}
                    value={dayjs(vehicule.realTimeData.timestamp).format("DD-MM-YYYY HH:mm:ss")}
                  />
                )}
              </div>
            </div>

            {/* Location Information */}
            {(vehicule.realTimeData.latitude && vehicule.realTimeData.longitude) && (
              <>
                <div className={classes.paperTitle} style={{ marginTop: '16px' }}>
                  <CustomTypography
                    isUppercase
                    variant="h3"
                    text={t("vehicule.location", "Location")}
                  />
                </div>
                <div className={classes.mark}>
                  <div>
                    <CustomTypographyLabelValue
                      label={t("vehicule.latitude", "Latitude")}
                      value={vehicule.realTimeData.latitude.toFixed(6)}
                    />
                    <CustomTypographyLabelValue
                      label={t("vehicule.longitude", "Longitude")}
                      value={vehicule.realTimeData.longitude.toFixed(6)}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
        </div>
      </div>

      {/*
        Full density, on its own row under the card body: this is the one
        screen about a SINGLE vehicle, so it is where the whole sentence
        belongs and the only place the plan upsell is allowed to appear. The
        list column deliberately suppresses `plan_excluded` - repeating the
        same upgrade prompt on every row would be nagging. Here it is
        answering a question the user is actually asking about this car.

        It sits OUTSIDE the plate tile on purpose. The tile is a fixed
        182x182 centered box sized for the registration plate alone; a
        wrapping sentence dropped in beside it had nowhere to go and rendered
        on top of the plate. Full width also means the sentence fits on one
        line instead of breaking into a five-line paragraph.
      */}
      <TelematicsBadge
        telematics={(vehicule as any)?.telematics}
        density="full"
        showPlanUpsell
      />
    </div>
  );
};